import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Edit, Trash2, Search, Building2, Phone, Bell, BellOff, AlertTriangle } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Usuarios = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.isSuperAdmin === true;
  const [usuarios, setUsuarios] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [filteredUsuarios, setFilteredUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [rolFilter, setRolFilter] = useState('all');
  const [formData, setFormData] = useState({ nombre: '', email: '', password: '', rol: 'Agente', sucursalId: '', whatsapp: '', notificacionesWhatsapp: true });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [usuarios, searchTerm, rolFilter]);

  const loadData = async () => {
    try {
      const [usuariosRes, sucursalesRes] = await Promise.all([
        axios.get(`${API}/usuarios`),
        axios.get(`${API}/sucursales`),
      ]);
      setUsuarios(usuariosRes.data);
      setSucursales(sucursalesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const getSucursalNombre = (sucursalId) => {
    const sucursal = sucursales.find((s) => s.id === sucursalId);
    return sucursal ? sucursal.nombre : '-';
  };

  const applyFilters = () => {
    let filtered = [...usuarios];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((u) => u.nombre.toLowerCase().includes(term));
    }

    if (rolFilter !== 'all') {
      if (rolFilter === 'bloqueado_wa') {
        filtered = filtered.filter((u) => u.whatsappBloqueado);
      } else {
        filtered = filtered.filter((u) => u.rol === rolFilter);
      }
    }

    setFilteredUsuarios(filtered);
  };

  const handleOpenDialog = (usuario = null) => {
    if (usuario) {
      setEditingId(usuario.id);
      setFormData({ ...usuario, password: '', sucursalId: usuario.sucursalId || '', whatsapp: usuario.whatsapp || '', notificacionesWhatsapp: usuario.notificacionesWhatsapp !== false });
    } else {
      setEditingId(null);
      setFormData({ nombre: '', email: '', password: '', rol: 'Agente', sucursalId: '', whatsapp: '', notificacionesWhatsapp: true });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      if (!formData.nombre) {
        toast.error('El nombre es requerido');
        return;
      }
      if (!editingId && !formData.email) {
        toast.error('El email es requerido');
        return;
      }
      if (!editingId && !formData.password) {
        toast.error('La contraseña es requerida');
        return;
      }

      if (editingId) {
        // Para edición, solo enviar campos que tienen valor
        const updateData = { nombre: formData.nombre, rol: formData.rol };
        if (formData.email) updateData.email = formData.email;
        if (formData.password) updateData.password = formData.password;
        if (formData.sucursalId) updateData.sucursalId = formData.sucursalId;
        updateData.whatsapp = formData.whatsapp || '';
        updateData.notificacionesWhatsapp = formData.notificacionesWhatsapp;
        await axios.put(`${API}/usuarios/${editingId}`, updateData);
        toast.success('Usuario actualizado');
      } else {
        const createData = { ...formData };
        if (!createData.sucursalId) delete createData.sucursalId;
        await axios.post(`${API}/usuarios`, createData);
        toast.success('Usuario creado');
      }
      setShowDialog(false);
      loadData();
    } catch (error) {
      console.error('Error saving usuario:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar usuario');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/usuarios/${id}`);
      toast.success('Usuario eliminado');
      loadData();
    } catch (error) {
      console.error('Error deleting usuario:', error);
      toast.error('Error al eliminar usuario');
    }
    setDeleteId(null);
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><div className="text-muted-foreground">Cargando...</div></div>;
  }

  return (
    <div className="space-y-6 bg-background min-h-screen">
      <div className="bg-background border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight">Usuarios y Roles</h1>
              <p className="text-muted-foreground mt-1">{usuarios.length} usuarios</p>
            </div>
            <Button onClick={() => handleOpenDialog()}>
              <Plus className="h-4 w-4 mr-2" />Nuevo Usuario
            </Button>
          </div>
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Filtros */}
        <Card className="p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-user-input"
              />
            </div>
            <Select value={rolFilter} onValueChange={setRolFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="rol-filter">
                <SelectValue placeholder="Rol" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los roles</SelectItem>
                <SelectItem value="Admin">Admin</SelectItem>
                <SelectItem value="Agente">Agente</SelectItem>
                <SelectItem value="SoloLectura">Solo Lectura</SelectItem>
                <SelectItem value="bloqueado_wa">Bloqueados por WA</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {filteredUsuarios.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-muted-foreground">
              {usuarios.length === 0 ? 'No hay usuarios' : 'No se encontraron resultados'}
            </p>
          </Card>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b">
                  <tr>
                    <th className="text-left p-4 font-medium">Nombre</th>
                    <th className="text-left p-4 font-medium">Email</th>
                    <th className="text-left p-4 font-medium">Rol</th>
                    <th className="text-left p-4 font-medium">Sucursal</th>
                    <th className="text-right p-4 font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsuarios.map((usuario) => (
                    <tr key={usuario.id} className="border-b last:border-0">
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          {usuario.nombre}
                          {usuario.whatsapp && usuario.notificacionesWhatsapp !== false && (
                            <Bell className="h-3 w-3 text-muted-foreground" title="Notificaciones WhatsApp activadas" />
                          )}
                          {usuario.whatsapp && usuario.notificacionesWhatsapp === false && (
                            <BellOff className="h-3 w-3 text-muted-foreground" title="Notificaciones WhatsApp desactivadas" />
                          )}
                          {usuario.whatsapp && usuario.whatsappBloqueado && (
                            <AlertTriangle className="h-3.5 w-3.5 text-destructive drop-shadow-sm" title="Número bloqueado temporalmente por API de WhatsApp (Ecosistema)" />
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-muted-foreground">{usuario.email}</td>
                      <td className="p-4">
                        <span className="inline-flex items-center rounded-md bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
                          {usuario.rol}
                        </span>
                      </td>
                      <td className="p-4">
                        {usuario.sucursalId ? (
                          <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                            <Building2 className="h-3 w-3" />
                            {getSucursalNombre(usuario.sucursalId)}
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        {!usuario.isProtected && (
                          <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => handleOpenDialog(usuario)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setDeleteId(usuario.id)} data-testid="user-delete-confirm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar' : 'Nuevo'} Usuario</DialogTitle>
            <DialogDescription>Completa la información del usuario</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre *</Label>
              <Input id="nombre" value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email {!editingId && '*'}</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="usuario@email.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">
                Contraseña {editingId ? '(dejar vacío para no cambiar)' : '*'}
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={editingId ? 'Nueva contraseña (opcional)' : 'Contraseña'}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rol">Rol</Label>
              <Select value={formData.rol} onValueChange={(value) => setFormData({ ...formData, rol: value })} data-testid="user-role-select">
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {isSuperAdmin && <SelectItem value="SuperAdmin">SuperAdmin</SelectItem>}
                  <SelectItem value="Admin">Admin</SelectItem>
                  <SelectItem value="Agente">Agente</SelectItem>
                  <SelectItem value="SoloLectura">Solo Lectura</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="sucursal">Sucursal</Label>
              <Select
                value={formData.sucursalId || 'none'}
                onValueChange={(value) => setFormData({ ...formData, sucursalId: value === 'none' ? '' : value })}
                data-testid="user-sucursal-select"
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar sucursal..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin sucursal asignada</SelectItem>
                  {sucursales.filter(s => s.activa).map((sucursal) => (
                    <SelectItem key={sucursal.id} value={sucursal.id}>
                      {sucursal.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="whatsapp">
                <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> Celular (WhatsApp)</span>
              </Label>
              <Input
                id="whatsapp"
                value={formData.whatsapp}
                onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                placeholder="59899123456"
              />
              <p className="text-xs text-muted-foreground">Se utiliza para enviar notificaciones de ofertas por WhatsApp. Formato: código de país + número, sin espacios ni guiones.</p>
            </div>
            {formData.whatsapp && (
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <Label htmlFor="notificacionesWhatsapp" className="text-sm font-medium cursor-pointer">
                    Recibir notificaciones por WhatsApp
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">Recibir alertas de nuevas ofertas</p>
                </div>
                <Switch
                  id="notificacionesWhatsapp"
                  checked={formData.notificacionesWhatsapp}
                  onCheckedChange={(checked) => setFormData({ ...formData, notificacionesWhatsapp: checked })}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
            <Button onClick={handleSave}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar usuario?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteId)}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Usuarios;